import { field } from '../fields'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Search, Users } from 'lucide-react'
import { fetchContent, text } from '../api/content'
import { useCan } from '../useSession'
import {
  ageOn,
  dateOfBirthProblem,
  fetchHousehold,
  saveDancer,
  saveGuardian,
  saveHousehold,
  searchHouseholds,
  type HouseholdDancer,
  type HouseholdGuardian,
  type HouseholdView,
  fetchInviteState,
  inviteGuardian,
} from '../api/peopleAdmin'

/**
 * Correcting what the studio knows about a family.
 *
 * **Everything here is genuinely editable**, unlike a class (D-124): the
 * importer creates people and never updates them, so nothing typed here is
 * overwritten by the next import.
 *
 * The cost runs the other way, and the screen says so — a correction here does
 * not reach Jackrabbit, which still owns billing (D-111). Implying a single
 * source that does not exist is how somebody ends up believing a new address
 * reached an invoice.
 *
 * <b>Which family is open lives in the URL, not in component state</b> (D-185).
 * The dancer record links straight here with `?household=…`, and while the open
 * family was local state that link silently landed on the search list showing
 * all 400-odd families — the one thing the person clicking "Open family" was
 * not asking for.
 */
export default function PeopleAdminView() {
  const [query, setQuery] = useState('')
  const [params, setParams] = useSearchParams()
  const openId = params.get('household')

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['people-search', query],
    queryFn: () => searchHouseholds(query),
    // Keeps the list on screen while a new search runs, so typing does not
    // flash the page empty between keystrokes.
    placeholderData: (previous) => previous,
    // Nothing to search while a family is open, and re-running it on every
    // keystroke typed before opening would be work nobody sees.
    enabled: openId === null,
  })

  const open = (householdId: string) => {
    const next = new URLSearchParams(params)
    next.set('household', householdId)
    // Pushed, so Back closes the family rather than leaving the screen.
    setParams(next)
  }

  const close = () => {
    const next = new URLSearchParams(params)
    next.delete('household')
    /*
     * Replaced rather than pushed. Arriving from a dancer record and pressing
     * "All families" should leave Back pointing at the dancer — pushing here
     * would make Back re-open the family that was just closed, which reads as
     * the button not working.
     */
    setParams(next, { replace: true })
  }

  if (openId) return <HouseholdEditor householdId={openId} onClose={close} />

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Studio</p>
        <h1 className="font-display text-2xl">Families</h1>
        <p className="mt-1.5 max-w-[40rem] text-[13.5px] text-ink-3">
          Everyone the studio has on file. Open a family to fix a spelling, a birthday or a
          contact detail &mdash; or search by any name, the family&rsquo;s, the dancer&rsquo;s or
          the parent&rsquo;s.
        </p>
      </header>

      <div className="flex items-center gap-2.5 rounded-xl border border-line-2 bg-surface px-3.5 py-2.5 focus-within:border-accent">
        <Search size={17} strokeWidth={2} className="shrink-0 text-ink-3" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          placeholder="Search families, dancers or parents"
          aria-label="Search people"
          className="min-w-0 flex-1 bg-transparent text-[14px] outline-none"
        />
      </div>

      {isPending && <p className="mt-3 text-[13px] text-ink-3">Loading...</p>}

      {data && (
        <p className="mt-3 text-[13px] text-ink-3 tabular">
          {query.trim()
            ? `${data.length} ${data.length === 1 ? 'family' : 'families'} matching "${query.trim()}"`
            : `${data.length} ${data.length === 1 ? 'family' : 'families'}`}
          {isFetching && <span className="ml-2 text-ink-3">searching...</span>}
        </p>
      )}

      {data?.length === 0 && !isPending && (
        <p className="mt-4 rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[13.5px] text-ink-3">
          {query.trim() ? 'Nobody by that name.' : 'No families yet — import from Jackrabbit to bring them across.'}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2">
        {data?.map((hit) => (
          <button
            key={hit.householdId}
            type="button"
            onClick={() => open(hit.householdId)}
            className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-left hover:border-ink-3"
          >
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-surface-2 text-ink-3">
              <Users size={16} strokeWidth={2} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-semibold wrap-anywhere">{hit.name}</span>
              {hit.members && (
                <span className="block text-[12.5px] text-ink-3 wrap-anywhere">{hit.members}</span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function HouseholdEditor({ householdId, onClose }: { householdId: string; onClose: () => void }) {
  const queryClient = useQueryClient()

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['household', householdId],
    queryFn: () => fetchHousehold(householdId),
  })

  const refresh = (updated: HouseholdView) =>
    queryClient.setQueryData(['household', householdId], updated)

  const back = (
    <button
      type="button"
      onClick={onClose}
      className="text-[13.5px] font-semibold text-ink-3 hover:text-ink"
    >
      &larr; All families
    </button>
  )

  if (isPending) return <p className="px-5 py-10 text-sm text-ink-3">Loading...</p>

  /*
   * A family id can now arrive in the URL (D-185), so it can now be wrong —
   * bookmarked before a merge, or typed. Saying so beats the previous
   * behaviour, which was an indistinguishable "Loading..." that never resolved.
   */
  if (isError || !data) {
    return (
      <div className="page-shell px-5 py-8">
        {back}
        <p className="mt-4 rounded-2xl border border-crit bg-crit-bg px-5 py-6 text-[13.5px] text-crit">
          {isError ? (error as Error).message : 'That family could not be found.'}
        </p>
      </div>
    )
  }

  return (
    <div className="page-shell px-5 py-8">
      {back}

      <h1 className="mt-4 mb-1 font-display text-xl wrap-anywhere">{data.name}</h1>

      {/*
        Said once, plainly. A correction here does not reach Jackrabbit, which
        still owns billing (D-111) — and implying otherwise is how somebody
        believes a new address reached an invoice.
      */}
      <p className="mb-5 text-[12.5px] text-ink-3">
        Changes here stay in this app. Anything that affects billing needs changing in Jackrabbit
        too.
      </p>

      <HouseholdDetails household={data} onSaved={refresh} />

      <h2 className="mt-7 mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Dancers
      </h2>
      <div className="flex flex-col gap-2.5">
        {data.dancers.map((dancer) => (
          <DancerRow key={dancer.dancerId} dancer={dancer} onSaved={refresh} />
        ))}
      </div>

      <h2 className="mt-7 mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Contacts
      </h2>
      <div className="flex flex-col gap-2.5">
        {data.guardians.map((guardian) => (
          <GuardianRow key={guardian.guardianId} guardian={guardian} onSaved={refresh} />
        ))}
      </div>
    </div>
  )
}
function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-2xl border border-line bg-surface px-4 py-3.5">{children}</div>
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-semibold text-ink-3">{label}</span>
      {children}
    </label>
  )
}

function SaveRow({
  dirty,
  busy,
  error,
  onSave,
  disabled,
}: {
  dirty: boolean
  busy: boolean
  error: string | null
  onSave: () => void
  disabled?: boolean
}) {
  return (
    <>
      {error && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={!dirty || busy || disabled}
        onClick={onSave}
        className="mt-3 rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
      >
        {busy ? 'Saving...' : dirty ? 'Save' : 'Saved'}
      </button>
    </>
  )
}

function HouseholdDetails({
  household,
  onSaved,
}: {
  household: HouseholdView
  onSaved: (updated: HouseholdView) => void
}) {
  const [form, setForm] = useState(household)
  useEffect(() => setForm(household), [household])

  const save = useMutation({
    mutationFn: () =>
      saveHousehold(household.householdId, {
        name: form.name,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
      }),
    onSuccess: onSaved,
  })

  const set = (patch: Partial<HouseholdView>) => setForm((f) => ({ ...f, ...patch }))
  const dirty = JSON.stringify(form) !== JSON.stringify(household)

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <Field label="Family name">
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} className={field} />
        </Field>

        <Field label="Address">
          <input
            value={form.addressLine1 ?? ''}
            onChange={(e) => set({ addressLine1: e.target.value || null })}
            className={field}
          />
        </Field>

        <div className="flex flex-wrap gap-2.5">
          <div className="min-w-[8rem] flex-1">
            <Field label="City">
              <input
                value={form.city ?? ''}
                onChange={(e) => set({ city: e.target.value || null })}
                className={field}
              />
            </Field>
          </div>

          <div className="w-[5rem]">
            <Field label="State">
              <input
                value={form.state ?? ''}
                onChange={(e) => set({ state: e.target.value || null })}
                className={field}
              />
            </Field>
          </div>

          <div className="w-[7rem]">
            <Field label="ZIP">
              <input
                value={form.postalCode ?? ''}
                onChange={(e) => set({ postalCode: e.target.value || null })}
                className={field}
              />
            </Field>
          </div>
        </div>
      </div>

      <SaveRow
        dirty={dirty}
        busy={save.isPending}
        error={save.isError ? (save.error as Error).message : null}
        onSave={() => save.mutate()}
        disabled={form.name.trim().length === 0}
      />
    </Card>
  )
}

function DancerRow({
  dancer,
  onSaved,
}: {
  dancer: HouseholdDancer
  onSaved: (updated: HouseholdView) => void
}) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(dancer)
  useEffect(() => setForm(dancer), [dancer])

  const save = useMutation({
    mutationFn: () =>
      saveDancer(dancer.dancerId, {
        firstName: form.firstName,
        lastName: form.lastName,
        dateOfBirth: form.dateOfBirth,
        notes: null,
      }),
    onSuccess: (updated) => {
      onSaved(updated)
      setOpen(false)
    },
  })

  const problem = dateOfBirthProblem(form.dateOfBirth)
  const age = ageOn(dancer.dateOfBirth)
  const dirty = JSON.stringify(form) !== JSON.stringify(dancer)

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold">
            {dancer.firstName} {dancer.lastName}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {age !== null && <>Age {age} &middot; </>}
            {dancer.classes} {dancer.classes === 1 ? 'class' : 'classes'}
          </p>
        </div>

        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Edit
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2.5">
            <div className="min-w-[8rem] flex-1">
              <Field label="First name">
                <input
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  className={field}
                />
              </Field>
            </div>

            <div className="min-w-[8rem] flex-1">
              <Field label="Last name">
                <input
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  className={field}
                />
              </Field>
            </div>
          </div>

          <Field label="Date of birth">
            <input
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
              className={field}
            />
          </Field>

          {problem ? (
            <p className="text-[12px] text-crit">{problem}</p>
          ) : (
            // Named, because it is what decides which classes she can be in.
            <p className="text-[12px] text-ink-3">
              This decides which classes she is eligible for.
            </p>
          )}

          <div className="flex items-center gap-2">
            <SaveRow
              dirty={dirty}
              busy={save.isPending}
              error={save.isError ? (save.error as Error).message : null}
              onSave={() => save.mutate()}
              disabled={problem !== null || form.firstName.trim().length === 0}
            />

            <button
              type="button"
              onClick={() => {
                setForm(dancer)
                setOpen(false)
              }}
              className="mt-3 text-[12.5px] font-semibold text-ink-3"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  )
}

function GuardianRow({
  guardian,
  onSaved,
}: {
  guardian: HouseholdGuardian
  onSaved: (updated: HouseholdView) => void
}) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(guardian)
  useEffect(() => setForm(guardian), [guardian])

  const canReview = useCan('ReviewConversations')
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const save = useMutation({
    mutationFn: () =>
      saveGuardian(guardian.guardianId, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email?.trim() || null,
        mobilePhone: form.mobilePhone?.trim() || null,
        relationship: form.relationship?.trim() || null,
      }),
    onSuccess: (updated) => {
      onSaved(updated)
      setOpen(false)
    },
  })

  const dirty = JSON.stringify(form) !== JSON.stringify(guardian)

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        {/*
          <b>A floor under the name, so the row wraps instead of crushing it</b>
          (D-631). This was `min-w-0 flex-1`, which shrinks without limit: two
          controls fitted beside it at 390px and the third broke her name across
          two lines, split her address mid-word and pushed her phone number off
          the card. A flex row cannot wrap while a child will still shrink, so
          the minimum is what makes `flex-wrap` above do anything. Rows carrying
          one control still sit inline, which is most of them. Measured at 390px
          before and after; nothing about this was visible from the source.
        */}
        <div className="min-w-[11rem] flex-1">
          <p className="text-[14px] font-semibold">
            {guardian.firstName} {guardian.lastName}
            {guardian.relationship && (
              <span className="ml-2 text-[12px] font-normal text-ink-3">{guardian.relationship}</span>
            )}
          </p>
          {guardian.email && <p className="text-[12.5px] text-ink-3 wrap-anywhere">{guardian.email}</p>}

          {/*
            Said here rather than left to be discovered at the invite (D-834). The
            edit screen used to refuse a shared address outright; now it records it
            and the login goes to whoever already had it, so the row has to say
            which of those happened. A control that silently does something
            narrower than what was typed is the failure this prevents.

            ⚠ It states what `canSignIn` actually answers and no more. An earlier
            version read "another contact signs in with this address", which is a
            *cause* the payload cannot support: `canSignIn` says she has no login,
            not why. Three ways to reach that state without a rival - a guardian
            created by `/api/register`, one whose rival's address was later edited
            away, and one the import left contact-only - so the sentence would have
            sent Tonya looking for a duplicate that is not there (D-209).
          */}
          {guardian.email && !guardian.canSignIn && (
            <p className="text-[12.5px] text-ink-3">Contact only &mdash; no sign-in</p>
          )}

          {guardian.mobilePhone && <p className="text-[12.5px] text-ink-3">{guardian.mobilePhone}</p>}
        </div>

        {!open && (
          <div className="flex shrink-0 items-center gap-2">
            {/*
              One family at a time, deliberately (D-155). Most families arrive
              through the public claim page; this is for starting a pilot with a
              handful, and for the parent who rings up saying it never worked.
            */}
            {guardian.email && <InviteButton guardianId={guardian.guardianId} />}

            {/*
              Every conversation this account is part of, read from outside
              (D-631). Only where there is an account to be in one, and only
              for somebody who may review - the same two conditions the staff
              screen carries, for the same reason: a link that opens onto a
              refusal is worse than none.
            */}
            {canReview && guardian.hasAccount && (
              <Link
                to={`/admin/guardians/${guardian.guardianId}/conversations`}
                className="flex items-center gap-1.5 rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
              >
                <MessageSquare size={13} strokeWidth={2.5} />
                {text(content, 'messages.review.heading', 'Conversations')}
              </Link>
            )}

            <button
              type="button"
              onClick={() => setOpen(true)}
              className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2.5">
            <div className="min-w-[8rem] flex-1">
              <Field label="First name">
                <input
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  className={field}
                />
              </Field>
            </div>

            <div className="min-w-[8rem] flex-1">
              <Field label="Last name">
                <input
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  className={field}
                />
              </Field>
            </div>
          </div>

          <Field label="Email">
            <input
              value={form.email ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value || null }))}
              className={field}
            />
          </Field>
          {/* Email is the login (Q-1), which is why it cannot be shared. */}
          <p className="-mt-1.5 text-[12px] text-ink-3">This is how they sign in.</p>

          <div className="flex flex-wrap gap-2.5">
            <div className="min-w-[9rem] flex-1">
              <Field label="Mobile">
                <input
                  value={form.mobilePhone ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, mobilePhone: e.target.value || null }))}
                  className={field}
                />
              </Field>
            </div>

            <div className="min-w-[9rem] flex-1">
              <Field label="Relationship">
                <input
                  value={form.relationship ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, relationship: e.target.value || null }))}
                  placeholder="Mother"
                  className={field}
                />
              </Field>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <SaveRow
              dirty={dirty}
              busy={save.isPending}
              error={save.isError ? (save.error as Error).message : null}
              onSave={() => save.mutate()}
              disabled={form.firstName.trim().length === 0}
            />

            <button
              type="button"
              onClick={() => {
                setForm(guardian)
                setOpen(false)
              }}
              className="mt-3 text-[12.5px] font-semibold text-ink-3"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  )
}

/**
 * Sends one family their login link (D-155).
 *
 * **Shows why it cannot, rather than being greyed out with no explanation.**
 * "Already has a login" and "invited last Tuesday" lead to completely different
 * next actions, and a disabled button says neither.
 */
function InviteButton({ guardianId }: { guardianId: string }) {
  const queryClient = useQueryClient()

  const { data: state } = useQuery({
    queryKey: ['invite-state', guardianId],
    queryFn: () => fetchInviteState(guardianId),
  })

  const send = useMutation({
    mutationFn: () => inviteGuardian(guardianId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invite-state', guardianId] }),
  })

  if (!state) return null

  if (state.skip === 'AlreadyRegistered') {
    return (
      <span className="text-[12px] font-semibold text-good" title="They can already sign in">
        Has a login
      </span>
    )
  }

  if (state.skip === 'NotASignIn') {
    return (
      <span className="text-[12px] text-ink-3" title="Marked as not a sign-in contact">
        Not a login
      </span>
    )
  }

  const invited = state.skip === 'AlreadyInvited'

  return (
    <div className="flex items-center gap-2">
      {send.isError && (
        <span role="alert" className="text-[12px] text-crit">
          {(send.error as Error).message}
        </span>
      )}

      {send.isSuccess && !send.isPending && (
        <span className="text-[12px] font-semibold text-good">Sent</span>
      )}

      <button
        type="button"
        onClick={() => send.mutate()}
        disabled={send.isPending}
        title={
          invited
            ? 'Already invited. Sending again issues a fresh link and cancels the old one.'
            : 'Emails them a link to set a password'
        }
        className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2 disabled:opacity-50"
      >
        {send.isPending ? 'Sending...' : invited ? 'Invite again' : 'Send login invite'}
      </button>
    </div>
  )
}
